import { YallyLogo } from "assets/index.ts";
import React, { useMemo, useState, useEffect, useContext, useRef } from "react";
import { categorizedRoutesConfig, standaloneRoutes, type RouteItem, type RouteCategory } from "shared/config/routes.tsx";
import { useAssetCategories } from "hooks/useAssets";
import { Package } from "@phosphor-icons/react";
import { getIconByValue } from "components/dueDiligence/IconPicker";
import { useWindowResize } from "hooks/useWindowResize.ts";
import { MobileNavMenu } from "shared/components/navMenu/mobileNavMenu.tsx";
import { UserButton, useSession, useUser } from "@clerk/clerk-react";
import { usePermissions } from "../../contexts/permissions.context";
import { Tooltip, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button, Popover, PopoverTrigger, PopoverContent, Switch } from "@heroui/react";
import { CaretDown, Bell, Plus, Money, Signature, ArrowSquareOut, Buildings, CheckSquare, FileArrowUp, FileCloud, Image, Umbrella, User, WhatsappLogo, EnvelopeSimple, MagnifyingGlass, Toolbox, Command, DotsThree, SirenIcon } from "@phosphor-icons/react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { MenuContext } from "shared/components/menuProvider/menuProvider.tsx";
import DirectSearchComponent, { DirectSearchComponentRef } from "../searchBar/components/directSearchComponent";
import { useKeyboardShortcut } from 'shared/hooks/useKeyboardShortcut';
import { KeyboardShortcutsModal } from 'shared/components/keyboardShortcuts/KeyboardShortcutsModal';
import { QuickEllaPrompt } from 'shared/components/QuickEllaPrompt';

const getDesktopSubmenuLinkStyles = (isActive: boolean) => {
  return `py-1.5 px-3 flex items-center gap-1.5 text-sm font-medium transition-colors rounded-full whitespace-nowrap ${
    isActive
      ? 'bg-brick-primary/10 text-brick-primary'
      : 'text-primary hover:bg-primary/10 hover:text-primary/80'
  }`;
};

export const getMobileSubmenuLinkStyles = (isActive: boolean) => {
  return `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
    isActive
      ? 'bg-brick-primary/20 text-brick-primary'
      : 'text-gray-700 hover:bg-gray-900/20 hover:text-gray-900'
  }`;
};

const containerClassnames = () =>
  "fixed top-0 left-0 right-0 z-50 flex flex-col bg-muted w-full border-b border-neutral-primary/15 shadow-sm";

const NavBar = () => {
  const { x } = useWindowResize();
  const { hasPermission, isLoading } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useSession();
  const { categories: assetCategories } = useAssetCategories();
  const { user } = useUser();
  const { dispatch, onOpenAcquisition, openDocumentUpload, openPhotoUpload } = useContext(MenuContext);

  
  // Notifications state
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [showOnlyUnread, setShowOnlyUnread] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  // WhatsApp unread state
  const [whatsappUnreadCount, setWhatsappUnreadCount] = useState(0);
  
  // Search state
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchComponentRef = useRef<DirectSearchComponentRef>(null);
  
  // Keyboard shortcuts modal state
  const [keyboardShortcutsOpen, setKeyboardShortcutsOpen] = useState(false);

  // Quick Ella prompt state
  const [quickEllaOpen, setQuickEllaOpen] = useState(false);
  
  // Header height tracking
  const headerRef = useRef<HTMLDivElement>(null);
  
  // Responsive navigation state - simple window width based approach
  const [visibleNavItems, setVisibleNavItems] = useState<RouteItem[]>([]);
  const [collapsedNavItems, setCollapsedNavItems] = useState<RouteItem[]>([]);

  // Global search keyboard shortcut
  useKeyboardShortcut('shift+s', () => {
    if (!searchOpen) {
      setSearchOpen(true);
      // Focus search input after opening - increased timeout to ensure DOM is ready
      setTimeout(() => {
        searchComponentRef.current?.focusInput?.();
      }, 200);
    } else {
      searchComponentRef.current?.focusInput?.();
    }
  }, [], 'Focus global search', 'global');

  // Quick Ella prompt keyboard shortcut (CMD+E / CTRL+E)
  useKeyboardShortcut('mod+e', () => {
    setQuickEllaOpen(true);
  }, [], 'Open Ella quick prompt', 'global');

  // Notifications logic (extracted from searchBar)
  const displayedNotifications = notifications
    .filter((notif) => (showOnlyUnread ? !notif.readByUsers?.includes(user?.id) : true));

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!session || !user?.id) return;
      try {
        const res = await fetchWithAuth(`${API_URL}/notifications/get-notifications`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user.id }),
        }, session);
        if (res.ok) {
          const data = await res.json();
          setNotifications(data);
        }
      } catch (err) {
        console.error("Error fetching notifications:", err);
      }
    };
    fetchNotifications();
    const intervalId = setInterval(fetchNotifications, 60000);
    return () => clearInterval(intervalId);
  }, [session, user?.id]);

  // Update unread count
  useEffect(() => {
    setUnreadCount(notifications.filter((notif) => !notif.readByUsers?.includes(user?.id)).length);
  }, [notifications, user?.id]);

  // Fetch WhatsApp unread count (poll + event-driven instant refresh)
  useEffect(() => {
    const fetchWhatsappUnread = async () => {
      if (!session || !user?.id) return;
      try {
        const res = await fetchWithAuth(
          `${API_URL}/whatsapp/unread-count?userId=${encodeURIComponent(user.id)}`,
          { method: "GET" },
          session,
        );
        if (res.ok) {
          const data = await res.json();
          setWhatsappUnreadCount(data?.totalUnread ?? 0);
        }
      } catch (err) {
        console.error("Error fetching WhatsApp unread count:", err);
      }
    };
    fetchWhatsappUnread();
    const intervalId = setInterval(fetchWhatsappUnread, 60000);
    const onUnreadChanged = () => fetchWhatsappUnread();
    window.addEventListener("whatsapp:unread-changed", onUnreadChanged);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener("whatsapp:unread-changed", onUnreadChanged);
    };
  }, [session, user?.id]);

  // Click outside handler for search
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setSearchOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  const markAsRead = async (id: number) => {
    if (!session || !user?.id) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/notifications/mark-as-read/${id}`, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id }),
      }, session);
      if (res.ok) {
        setNotifications((prev: any[]) =>
          prev.map((n) => (n.id === id ? { ...n, readByUsers: [...(n.readByUsers || []), user.id] } : n))
        );
      }
    } catch (err) {
      console.error("Error marking notification as read", err);
    }
  };

  const markAllAsRead = async () => {
    if (!session || !user?.id) return;
    try {
      await fetchWithAuth(`${API_URL}/notifications/mark-all-as-read`, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id }),
      }, session);
      setNotifications((prev: any[]) =>
        prev.map((n) => ({ ...n, readByUsers: [...(n.readByUsers || []), user.id] }))
      );
    } catch (err) {
      console.error("Error marking all notifications as read", err);
    }
  };

  const handleOpenEsign = () => {
    window.location.href = `https://portal.yally.be/web#action=227&model=sign.request&view_type=kanban&menu_id=155&cids=1%2C2%2C3%2C4`;
  };

  // Filter categorized routes based on permissions
  const filteredCategorizedRoutes = useMemo(() => {
    if (isLoading) return [];

    const filterRoutes = (routes: RouteItem[]): RouteItem[] => {
      return routes.reduce((acc: RouteItem[], route) => {
        const hasMainPermission = !route.permission || hasPermission(route.permission as any);
        if (!hasMainPermission) return acc;
        if (route.showInNav === false) return acc;

        let filteredChildren: RouteItem[] | undefined;
        if (route.children) {
          filteredChildren = filterRoutes(route.children);
          filteredChildren = filteredChildren.length > 0 ? filteredChildren : undefined;
        }

        if (hasMainPermission) {
          acc.push({
            ...route,
            children: filteredChildren
          });
        }

        return acc;
      }, []);
    };

    // Build dynamic category routes from asset categories with slugs
    const dynamicCategoryRoutes: RouteItem[] = (assetCategories || [])
      .filter(c => c.active && (c as any).slug)
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map(c => {
        const IconComp = c.icon ? getIconByValue(c.icon) : null;
        return {
          to: `/assets/category/${(c as any).slug}`,
          label: c.name,
          icon: IconComp ? <IconComp weight="duotone" size={20} /> : <Package weight="duotone" size={20} />,
          permission: "canAccessMaintenance" as const,
        };
      });

    return categorizedRoutesConfig.reduce((acc: RouteCategory[], category) => {
      // Skip categories that are explicitly hidden
      if (category.showInNav === false) return acc;

      let filteredRoutes = filterRoutes(category.routes);

      // Inject dynamic asset category routes into the "Onderhoud" category
      if (category.label === 'Onderhoud' && dynamicCategoryRoutes.length > 0) {
        filteredRoutes = [...filteredRoutes, ...dynamicCategoryRoutes];
      }

      if (filteredRoutes.length > 0) {
        acc.push({
          ...category,
          routes: filteredRoutes
        });
      }
      return acc;
    }, []);
  }, [hasPermission, isLoading, assetCategories]);

  // Filter standalone routes based on permissions
  const filteredStandaloneRoutes = useMemo(() => {
    if (isLoading) return [];
    
    const filterRoutes = (routes: RouteItem[]): RouteItem[] => {
      return routes.reduce((acc: RouteItem[], route) => {
        const hasMainPermission = !route.permission || hasPermission(route.permission as any);
        if (!hasMainPermission) return acc;

        let filteredChildren: RouteItem[] | undefined;
        if (route.children) {
          filteredChildren = filterRoutes(route.children);
          filteredChildren = filteredChildren.length > 0 ? filteredChildren : undefined;
        }

        if (hasMainPermission) {
          acc.push({
            ...route,
            children: filteredChildren
          });
        }

        return acc;
      }, []);
    };

    return filterRoutes(standaloneRoutes);
  }, [hasPermission, isLoading]);

  // Combine all navigation items for responsive behavior
  const allNavItems = useMemo(() => {
    const items: (RouteItem | RouteCategory)[] = [];
    
    // Add categorized routes
    filteredCategorizedRoutes.forEach(category => {
      items.push(category);
    });
    
    // Add standalone routes
    filteredStandaloneRoutes.forEach(route => {
      items.push(route);
    });
    
    return items;
  }, [filteredCategorizedRoutes, filteredStandaloneRoutes]);

  // Simple responsive navigation logic based on window width
  useEffect(() => {
    if (!allNavItems.length) return;

    // Simple breakpoints based on window width - much more generous
    let maxVisibleItems;
    if (x < 1000) {
      maxVisibleItems = 5; // Show 5 items + More dropdown
    } else if (x < 1200) {
      maxVisibleItems = 7; // Show 7 items + More dropdown  
    } else if (x < 1500) {
      maxVisibleItems = 9; // Show 9 items + More dropdown
    } else if (x < 1800) {
      maxVisibleItems = 11; // Show 11 items + More dropdown
    } else {
      maxVisibleItems = allNavItems.length; // Show all items at 1800px+
    }
    
    if (maxVisibleItems >= allNavItems.length) {
      // All items fit
      setVisibleNavItems(allNavItems as any);
      setCollapsedNavItems([]);
    } else {
      // Some items need to be collapsed
      const visible = allNavItems.slice(0, maxVisibleItems);
      const collapsed = allNavItems.slice(maxVisibleItems);
      setVisibleNavItems(visible as any);
      setCollapsedNavItems(collapsed as any);
    }
  }, [allNavItems, x]); // Re-run when window width or nav links change

  // Helper function to match route patterns with parameters (e.g., /building/:id)
  const matchesRoute = (routePath: string, currentPath: string): boolean => {
    // Exact match
    if (routePath === currentPath) return true;
    
    // Pattern matching for dynamic segments (e.g., :id, :buildingId)
    const routeParts = routePath.split('/');
    const currentParts = currentPath.split('/');
    
    if (routeParts.length !== currentParts.length) return false;
    
    return routeParts.every((part, index) => {
      // Match dynamic segments (starts with :)
      if (part.startsWith(':')) return true;
      // Match exact segments
      return part === currentParts[index];
    });
  };

  // Find current category or route with subitems
  const currentSubnavConfig = useMemo(() => {
    const currentPath = location.pathname;
    
    // First, check if we're in a category (show all routes in that category)
    for (const category of categorizedRoutesConfig) {
      const isInCategory = category.routes.some(route => 
        matchesRoute(route.to, currentPath) || 
        (route.children && route.children.some(child => matchesRoute(child.to, currentPath)))
      );
      
      if (isInCategory && category.routes.length > 1) {
        // Return a pseudo-route that represents the category
        return {
          type: 'category' as const,
          label: category.label,
          icon: category.icon,
          routes: category.routes
        };
      }
    }
    
    // Find the route that matches the current path and has children
    const findRouteWithChildren = (routes: RouteItem[]): RouteItem | null => {
      for (const route of routes) {
        // Check if current path matches this route and it has children
        if (matchesRoute(route.to, currentPath) && route.children && route.children.length > 0) {
          return route;
        }
        
        // Also check if we're on a child route, then return the parent
        if (route.children) {
          const hasChildMatch = route.children.some(child => matchesRoute(child.to, currentPath));
          if (hasChildMatch) {
            return route;
          }
        }
      }
      return null;
    };
    
    // Search in ALL categorized routes (including hidden ones) for subnav detection
    for (const category of categorizedRoutesConfig) {
      const found = findRouteWithChildren(category.routes);
      if (found) return { type: 'route' as const, route: found };
    }
    
    // Search in standalone routes
    const foundStandalone = findRouteWithChildren(standaloneRoutes);
    if (foundStandalone) return { type: 'route' as const, route: foundStandalone };
    
    return null;
  }, [location.pathname]);

  // Dynamic header height calculation
  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }
    };

    // Update initially and whenever the route changes (which might show/hide subnavigation)
    updateHeaderHeight();
    
    // Also update after a short delay to account for any animations or layout shifts
    const timeoutId = setTimeout(updateHeaderHeight, 100);
    
    return () => clearTimeout(timeoutId);
  }, [location.pathname, currentSubnavConfig]); // Re-run when route or subitems change

  if (x < 640) {
    return <MobileNavMenu />;
  }

  return (
    <React.Fragment>
      {/* Add body padding to account for fixed header and responsive nav animations */}
      <style>{`
        body {
          padding-top: var(--header-height, 70px);
        }
        .animate-fade-in {
          animation: fadeIn 0.2s ease-in-out;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
      
      {/* Main Navigation Bar */}
      <div
        ref={headerRef}
        id="navBar"
        className={containerClassnames()}
      >
        {/* Main Header Row */}
        <div className="flex justify-between py-3 px-[20px]">
          {/* Logo Section - Left */}
          <div className="flex mr-4">
            <a
              href="/"
            >
              <YallyLogo />
            </a>
          </div>

          {/* Navigation Links - Center */}
          <div className="flex items-center flex-1">
             <nav className="flex gap-6">
               {/* Render visible navigation items */}
               {visibleNavItems.map((item) => {
                 // Type guard to check if item is a category
                 const isCategory = 'routes' in item;
                 
                 if (isCategory) {
                   const category = item as RouteCategory;
                   // Check if any route in this category is active
                   const isActive = category.routes.some(route => 
                     location.pathname === route.to || 
                     (route.children && route.children.some(child => child.to === location.pathname))
                   );
                   
                   return (
                     <Dropdown key={category.label}>
                       <DropdownTrigger>
                         <button className={`p-2 flex text-foreground items-center gap-1 hover:text-primary-500 transition-colors rounded-full ${
                           isActive ? 'text-brick-primary bg-brick-primary/20' : ''
                         }`}>
                           <Tooltip content={category.label}>
                             <span className="flex items-center gap-1">
                               {React.cloneElement(category.icon, { weight: isActive ? 'bold' : 'duotone' })}
                               <CaretDown size={12} weight={isActive ? 'bold' : 'regular'} />
                             </span>
                           </Tooltip>
                         </button>
                       </DropdownTrigger>
                      <DropdownMenu aria-label={`${category.label} submenu`}>
                        {category.routes.map((route) => (
                          <DropdownItem key={route.to} textValue={route.label}>
                            <a href={route.to} className="flex items-center gap-2 w-full">
                              {React.cloneElement(route.icon, { 
                                weight: location.pathname === route.to ? 'bold' : 'duotone' 
                              })}
                              {route.label}
                            </a>
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                     </Dropdown>
                   );
                 } else {
                   // Regular route
                   const route = item as RouteItem;
                   const isActive = location.pathname === route.to || 
                     (route.children && route.children.some(child => child.to === location.pathname));
                   
                   // If route has children, render dropdown
                   if (route.children && route.children.length > 0) {
                     return (
                       <Dropdown key={route.to}>
                         <DropdownTrigger>
                           <button className={`p-2 flex items-center gap-1 hover:text-primary-500 transition-colors rounded-full ${
                             isActive ? 'text-brick-primary bg-brick-primary/20' : ''
                           }`}>
                             <Tooltip content={route.label}>
                               <span className="flex items-center gap-1">
                                 {React.cloneElement(route.icon, { weight: isActive ? 'bold' : 'duotone' })}
                                 <CaretDown size={12} weight={isActive ? 'bold' : 'regular'} />
                               </span>
                             </Tooltip>
                           </button>
                         </DropdownTrigger>
                         <DropdownMenu 
                           aria-label={`${route.label} submenu`}
                           items={[
                             ...(route.showParentInSubmenu !== false ? [route] : []),
                             ...(route.children || [])
                           ]}
                         >
                           {(item) => (
                             <DropdownItem key={item.to} textValue={item.label}>
                               <a href={item.to} className="flex items-center gap-2 w-full">
                                 {React.cloneElement(item.icon, { 
                                   weight: location.pathname === item.to ? 'bold' : 'duotone' 
                                 })}
                                 {item.label}
                               </a>
                             </DropdownItem>
                           )}
                         </DropdownMenu>
                       </Dropdown>
                     );
                   }
                   
                   // Regular link without children
                   return (
                     <Tooltip key={route.to} content={route.label}>
                         <a 
                           href={route.to}
                           className={`p-2 transition-colors rounded-full ${
                             isActive ? 'text-brick-primary bg-brick-primary/20' : 'hover:text-primary-500'
                           }`}
                         >
                           {React.cloneElement(route.icon, { weight: isActive ? 'bold' : 'duotone' })}
                         </a>
                     </Tooltip>
                   );
                 }
               })}
               
               {/* More dropdown for collapsed items */}
               {collapsedNavItems.length > 0 && (
                 <div className="animate-fade-in">
                   <Dropdown>
                     <DropdownTrigger>
                       <button className={`p-2 flex items-center gap-1 hover:text-primary-500 transition-colors rounded-full ${
                         collapsedNavItems.some(item => {
                           const isCategory = 'routes' in item;
                           if (isCategory) {
                             const category = item as RouteCategory;
                             return category.routes.some(route => 
                               location.pathname === route.to || 
                               (route.children && route.children.some(child => child.to === location.pathname))
                             );
                           } else {
                             const route = item as RouteItem;
                             return location.pathname === route.to || 
                               (route.children && route.children.some(child => child.to === location.pathname));
                           }
                         }) ? 'text-brick-primary bg-brick-primary/20' : ''
                       }`}>
                         <Tooltip content={`Meer menu's (${collapsedNavItems.length})`}>
                           <span className="flex items-center gap-1">
                             <DotsThree size={20} weight="bold" />
                             <CaretDown size={12} weight="regular" />
                           </span>
                         </Tooltip>
                       </button>
                     </DropdownTrigger>
                     <DropdownMenu aria-label="Meer navigatie items">
                       {collapsedNavItems.flatMap((item) => {
                         const isCategory = 'routes' in item;
                         
                        if (isCategory) {
                          const category = item as RouteCategory;
                          // For categories, show all routes in the category
                          return category.routes.map((route) => (
                            <DropdownItem key={route.to} textValue={route.label}>
                              <a href={route.to} className="flex items-center gap-2 w-full">
                                {React.cloneElement(route.icon, { 
                                  weight: location.pathname === route.to ? 'bold' : 'duotone' 
                                })}
                                <span className="text-xs text-gray-500 mr-2">{category.label}</span>
                                {route.label}
                              </a>
                            </DropdownItem>
                          ));
                         } else {
                           const route = item as RouteItem;
                           // If route has children, create a flat list including parent and children
                           const menuItems = [];
                           
                           // Add parent if it should be shown
                           if (route.showParentInSubmenu !== false) {
                             menuItems.push(route);
                           }
                           
                           // Add children
                           if (route.children) {
                             menuItems.push(...route.children);
                           } else if (route.showParentInSubmenu === false) {
                             // If parent shouldn't be shown but no children, still show the route
                             menuItems.push(route);
                           }
                           
                           return menuItems.map((item) => (
                             <DropdownItem key={item.to} textValue={item.label}>
                               <a href={item.to} className="flex items-center gap-2 w-full">
                                 {React.cloneElement(item.icon, { 
                                   weight: location.pathname === item.to ? 'bold' : 'duotone' 
                                 })}
                                 {item.label}
                               </a>
                             </DropdownItem>
                           ));
                         }
                       })}
                     </DropdownMenu>
                   </Dropdown>
                 </div>
               )}
            </nav>
          </div>

          {/* Tools and User Account - Right */}
          <div className="flex items-start gap-2 py-1.5">
            <div className="flex items-center gap-4">
            {/* WhatsApp */}
            <button
              onClick={() => navigate("/conversations")}
              className="p-2 relative h-fit"
              aria-label="WhatsApp"
            >
              <Tooltip content="WhatsApp gesprekken">
                <div>
                  <WhatsappLogo size={20} weight="duotone" />
                  {whatsappUnreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 z-10 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
                      {whatsappUnreadCount > 99 ? "99+" : whatsappUnreadCount}
                    </span>
                  )}
                </div>
              </Tooltip>
            </button>

            {/* Search */}
            <div className="relative" ref={searchRef}>
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="p-2 h-fit"
              >
                <Tooltip content="Zoeken">
                  <MagnifyingGlass size={20} weight="duotone" />
                </Tooltip>
              </button>
              
              {searchOpen && (
                <div className="absolute right-0 top-full z-50 mt-2 w-96">
                  <div className="rounded-lg border border-neutral-primary/20 bg-white p-4 shadow-lg">
                    <DirectSearchComponent 
                      ref={searchComponentRef}
                      onMobileNavSelect={() => setSearchOpen(false)}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Notifications */}
            <Popover isOpen={notifOpen} onOpenChange={setNotifOpen}>
                <PopoverTrigger>
                  <button className="p-2 relative h-fit">
                    <Tooltip content="Notificaties">
                      <div>
                        <Bell size={20} weight="duotone" />
                        {unreadCount > 0 && (
                          <span className="absolute -top-0 -right-0 z-10 block size-2 rounded-full bg-red-500">
                          </span>
                        )}
                      </div>
                    </Tooltip>
                  </button>
                </PopoverTrigger>
                <PopoverContent>
                  <div className="p-2 w-80">
                    <div className="flex items-center justify-between mb-2 space-x-2">
                      <span className="text-sm font-medium">
                        {showOnlyUnread ? "Alleen ongelezen" : "Alle notificaties"}
                      </span>
                      <Switch
                        size="sm"
                        isSelected={showOnlyUnread}
                        onChange={(e) => setShowOnlyUnread(e.target.checked)}
                      />
                      <Button 
                        size="sm"
                        className="text-xs bg-moss-primary text-white hover:bg-moss-primary/90" 
                        onClick={markAllAsRead}
                      >
                        Markeer alle als gelezen
                      </Button>
                    </div>
                    <ul className="max-h-72 overflow-y-auto">
                      {displayedNotifications.length === 0 ? (
                        <li className="p-2 text-center text-sm text-gray-500">
                          Geen notificaties
                        </li>
                      ) : (
                        displayedNotifications.map((notif) => (
                          <li
                            key={notif.id}
                            className={`p-2 border-b last:border-0 cursor-pointer ${
                              notif.readByUsers?.includes(user?.id) ? "bg-gray-100" : "bg-white"
                            }`}
                            onClick={() => {
                              if (!notif.readByUsers?.includes(user?.id)) markAsRead(notif.id);
                            }}
                          >
                            <div className={notif.readByUsers?.includes(user?.id) ? "text-gray-500" : "text-black font-bold"}>
                              {notif.url ? (
                                <div className="flex items-center">
                                  <span>{notif.message}</span>
                                  <a
                                    href={notif.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="ml-2 inline-flex items-center justify-center bg-moss-primary rounded-full p-1"
                                  >
                                    <ArrowSquareOut size={16} weight="duotone" className="text-white" />
                                  </a>
                                </div>
                              ) : (
                                notif.message
                              )}
                            </div>
                            {notif.fileNames && notif.fileNames.length > 0 && (
                              <div className="text-xs">
                                Bestanden: {notif.fileNames.join(", ")}
                              </div>
                            )}
                            <div className="text-xs text-gray-500">
                              {new Date(notif.createdAt).toLocaleString()}
                            </div>
                            {!notif.readByUsers?.includes(user?.id) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsRead(notif.id);
                                }}
                                className="text-xs text-brick-primary mt-1"
                              >
                                Markeer als gelezen
                              </button>
                            )}
                          </li>
                        ))
                      )}
                    </ul>
                    <div className="mt-2 border-t pt-2">
                      <button
                        onClick={() => {
                          setNotifOpen(false);
                          navigate("/meldingen");
                        }}
                        className="w-full rounded-md py-1.5 text-center text-sm font-medium text-moss-primary hover:bg-moss-primary/5"
                      >
                        Alle meldingen & acties bekijken
                      </button>
                    </div>
                  </div>
                </PopoverContent>
            </Popover>

            {/* Tools Dropdown */}
            <Dropdown>
              <DropdownTrigger>
                <button className="p-2 h-fit">
                  <Tooltip content="Tools">
                    <div className="flex items-center gap-1">
                      <Toolbox size={20} weight="duotone" />
                      <CaretDown size={12} weight="regular" />
                    </div>
                  </Tooltip>
                </button>
              </DropdownTrigger>
              <DropdownMenu 
                aria-label="Tools menu"
                onAction={(key) => {
                  if (key === "keyboard-shortcuts") {
                    setKeyboardShortcutsOpen(true);
                  }
                }}
              >
                <DropdownItem key="klantenfacturen" textValue="Klantenfacturen" className="p-0">
                  <a 
                    href="https://portal.yally.be/web#action=270&model=account.move&view_type=list&menu_id=291&cids=1%2C2%2C3%2C4" 
                    target="_blank"
                    className="flex items-center gap-2 w-full p-2"
                  >
                    <Money size={16} weight="duotone" />
                    Klantenfacturen
                  </a>
                </DropdownItem>
                <DropdownItem key="leveranciersfacturen" textValue="Leveranciersfacturen" className="p-0">
                  <a 
                    href="https://portal.yally.be/web#action=272&model=account.move&view_type=list&cids=1%2C3%2C2%2C4&menu_id=291" 
                    target="_blank"
                    className="flex items-center gap-2 w-full p-2"
                  >
                    <Money size={16} weight="duotone" />
                    Leveranciersfacturen
                  </a>
                </DropdownItem>
                <DropdownItem key="streetwisebelfius" textValue="Belfius - Streetwise NV" className="p-0">
                  <a 
                    href="https://portal.yally.be/web#cids=1%2C3%2C2%2C4&menu_id=291&active_id=14&model=account.bank.statement.line&view_type=kanban" 
                    target="_blank"
                    className="flex items-center gap-2 w-full p-2"
                  >
                    <Money size={16} weight="duotone" />
                    Belfius - Streetwise NV
                  </a>
                </DropdownItem>
                <DropdownItem key="immoalfabelfius" textValue="Belfius - Immo Alfa NV" className="p-0">
                  <a 
                    href="https://portal.yally.be/web#menu_id=291&cids=1%2C3%2C2%2C4&active_id=21&model=account.bank.statement.line&view_type=kanban" 
                    target="_blank"
                    className="flex items-center gap-2 w-full p-2"
                  >
                    <Money size={16} weight="duotone" />
                    Belfius - Immo Alfa NV
                  </a>
                </DropdownItem>
                <DropdownItem key="contracten" textValue="Huurcontracten" className="p-0">
                  <a 
                    href="https://portal.yally.be/web#menu_id=369&cids=1%2C3%2C2%2C4&action=1217&model=sale.order&view_type=kanban" 
                    target="_blank"
                    className="flex items-center gap-2 w-full p-2"
                  >
                    <Money size={16} weight="duotone" />
                    Huurcontracten
                  </a>
                </DropdownItem>
                <DropdownItem key="ledger" textValue="Grootboekschema" className="p-0">
                  <a 
                    href="https://portal.yally.be/web#action=280&model=account.account&view_type=list&menu_id=291&cids=1%2C3%2C2%2C4" 
                    target="_blank"
                    className="flex items-center gap-2 w-full p-2"
                  >
                    <Money size={16} weight="duotone" />
                    Grootboekschema
                  </a>
                </DropdownItem>
                <DropdownItem key="managerial-pl" textValue="Managerial Reporting - P&L" className="p-0">
                  <a 
                    href="https://portal.yally.be/web#menu_id=291&cids=1%2C2%2C3%2C4&action=1263" 
                    target="_blank"
                    className="flex items-center gap-2 w-full p-2"
                  >
                    <Money size={16} weight="duotone" />
                    Managerial Reporting - P&L
                  </a>
                </DropdownItem>
                <DropdownItem key="managerial-balans" textValue="Managerial Reporting - Balans" className="p-0">
                  <a 
                    href="https://portal.yally.be/web#menu_id=291&cids=1%2C2%2C3%2C4&action=1264" 
                    target="_blank"
                    className="flex items-center gap-2 w-full p-2"
                  >
                    <Money size={16} weight="duotone" />
                    Managerial Reporting - Balans
                  </a>
                </DropdownItem>
                <DropdownItem key="esign" textValue="E-Sign">
                  <button onClick={handleOpenEsign} className="flex items-center gap-2 w-full">
                    <Signature size={16} weight="duotone" />
                    E-Sign
                  </button>
                </DropdownItem>
                <DropdownItem key="keyboard-shortcuts" textValue="Keyboard Shortcuts">
                  <div className="flex items-center gap-2">
                    <Command size={16} weight="duotone" />
                    Sneltoetsen
                  </div>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>

             {/* Add/Voeg toe */}
             <Dropdown>
               <DropdownTrigger>
                 <button className="p-2 h-fit">
                   <Tooltip content="Voeg toe">
                     <div className="flex items-center gap-1">
                       <Plus size={20} />
                       <CaretDown size={12} weight="regular" />
                     </div>
                   </Tooltip>
                 </button>
               </DropdownTrigger>
              <DropdownMenu 
                aria-label="Voeg toe menu"
                onAction={(key) => {
                  switch (key) {
                    case "acquisitie":
                      onOpenAcquisition();
                      break;
                    case "helpdeskticket":
                      dispatch({ type: "helpdeskModal", payload: true });
                      break;
                    case "communicatie":
                      dispatch({ type: "startCommunicationModal", payload: true });
                      break;
                    case "verzekeringsdossier":
                      dispatch({ type: "addInsuranceCaseModal", payload: true });
                      break;
                    case "taak":
                      // Add delay to allow dropdown to close and release focus trap
                      setTimeout(() => {
                        if (location.pathname !== '/tasks' && !location.pathname.startsWith('/tasks/')) {
                          dispatch({ type: "taskModal", payload: true });
                        } else {
                          // Already on tasks page - dispatch event that tasks page will handle
                          window.dispatchEvent(new CustomEvent('openTaskModal'));
                        }
                      }, 100);
                      break;
                    case "contact":
                      navigate(`/contact/new?fresh=${Date.now()}`);
                      break;
                    case "bedrijf":
                      navigate(`/company/new`);
                      break;
                    case "upload-document":
                      openDocumentUpload();
                      break;
                    case "genereer-document":
                      navigate("/document-generator");
                      break;
                   case "mass-mail":
                     // Losse mass mail: geen module bepaalt de afzender, dus mag de
                     // gebruiker hier zelf kiezen ("Verzenden vanaf"-dropdown).
                     // Vanuit een module (helpdesk, reno, incasso, ...) staat het
                     // adres vast — die flows zetten deze vlag niet.
                     navigate("/mass-mail", { state: { allowSenderSelection: true } });
                     break;
                   case "mass-mail-legacy":
                     dispatch({ type: "massMailModal", payload: true });
                     break;
                    case "foto":
                      openPhotoUpload();
                      break;
                  }
                }}
               >
                 <DropdownItem key="acquisitie" textValue="Acquisitie">
                   <div className="flex items-center gap-2">
                     <Buildings size={16} weight="duotone" />
                     Acquisitie
                   </div>
                 </DropdownItem>
                 <DropdownItem key="helpdeskticket" textValue="Helpdeskticket">
                   <div className="flex items-center gap-2">
                     <SirenIcon size={16} weight="duotone" />
                     Helpdeskticket
                   </div>
                 </DropdownItem>
                 <DropdownItem key="communicatie" textValue="Communicatie">
                   <div className="flex items-center gap-2">
                     <WhatsappLogo size={16} weight="duotone" />
                     Communicatie
                   </div>
                 </DropdownItem>
                 <DropdownItem key="verzekeringsdossier" textValue="Verzekeringsdossier">
                   <div className="flex items-center gap-2">
                     <Umbrella size={16} weight="duotone" />
                     Verzekeringsdossier
                   </div>
                 </DropdownItem>
                 <DropdownItem key="taak" textValue="Taak">
                   <div className="flex items-center gap-2">
                     <CheckSquare size={16} weight="duotone" />
                     Taak
                   </div>
                 </DropdownItem>
                 <DropdownItem key="contact" textValue="Contact">
                   <div className="flex items-center gap-2">
                     <User size={16} weight="duotone" />
                     Contact
                   </div>
                 </DropdownItem>
                 <DropdownItem key="bedrijf" textValue="Bedrijf">
                   <div className="flex items-center gap-2">
                     <Buildings size={16} weight="duotone" />
                     Bedrijf
                   </div>
                 </DropdownItem>
                 <DropdownItem key="upload-document" textValue="Upload document">
                   <div className="flex items-center gap-2">
                     <FileArrowUp size={16} weight="duotone" />
                     Upload document
                   </div>
                 </DropdownItem>
                 <DropdownItem key="genereer-document" textValue="Genereer document">
                   <div className="flex items-center gap-2">
                     <FileCloud size={16} weight="duotone" />
                     Genereer document
                   </div>
                 </DropdownItem>
                 <DropdownItem key="mass-mail" textValue="Mass-mail">
                   <div className="flex items-center gap-2">
                     <EnvelopeSimple size={16} weight="duotone" />
                     E-mail sturen
                   </div>
                 </DropdownItem>
                 <DropdownItem key="foto" textValue="Foto">
                   <div className="flex items-center gap-2">
                     <Image size={16} weight="duotone" />
                     Foto
                   </div>
                 </DropdownItem>
               </DropdownMenu>
             </Dropdown>

            {/* User Account */}
            <UserButton />
          </div>
        </div>
        </div>
        
        {/* Subitems Navigation Bar - Show when in a category or on a route with children */}
        {currentSubnavConfig && (
          <nav className="flex gap-3 px-[20px] py-3 border-t border-primary-900/40 overflow-x-auto">
            {currentSubnavConfig.type === 'category' ? (
              // Show all routes in the category
              currentSubnavConfig.routes.map((route) => {
                const isActive = matchesRoute(route.to, location.pathname) || 
                  (route.children ? route.children.some(child => matchesRoute(child.to, location.pathname)) : false);
                
                return (
                  <a 
                    key={route.to}
                    href={route.to}
                    className={getDesktopSubmenuLinkStyles(isActive)}
                  >
                    {React.cloneElement(route.icon, { 
                      weight: isActive ? 'bold' : 'duotone' 
                    })}
                    {route.label}
                  </a>
                );
              })
            ) : (
              // Show route with its children (original behavior)
              <>
                {/* Parent route link - only show if showParentInSubmenu is not false */}
                {currentSubnavConfig.route.showParentInSubmenu !== false && (
                  <a
                    href={currentSubnavConfig.route.to}
                    className={getDesktopSubmenuLinkStyles(matchesRoute(currentSubnavConfig.route.to, location.pathname))}
                  >
                    {React.cloneElement(currentSubnavConfig.route.icon, { 
                      weight: matchesRoute(currentSubnavConfig.route.to, location.pathname) ? 'bold' : 'duotone' 
                    })}
                    {currentSubnavConfig.route.label}
                  </a>
                )}
                  
                {/* Child route links */}
                {currentSubnavConfig.route.children?.map((child) => {
                  // Replace route parameters with actual values from current URL
                  let childPath = child.to;
                  const currentParts = location.pathname.split('/');
                  const routeParts = child.to.split('/');
                  
                  routeParts.forEach((part, index) => {
                    if (part.startsWith(':') && currentParts[index]) {
                      childPath = childPath.replace(part, currentParts[index]);
                    }
                  });
                  
                  const isActive = matchesRoute(child.to, location.pathname);
                  
                  return (
                    <a 
                      key={child.to}
                      href={childPath}
                      className={getDesktopSubmenuLinkStyles(isActive)}
                    >
                      {React.cloneElement(child.icon, { 
                        weight: isActive ? 'bold' : 'duotone' 
                      })}
                      {child.label}
                    </a>
                  );
                })}
              </>
            )}
          </nav>
        )}
      </div>

      {/* Keyboard Shortcuts Modal */}
      <KeyboardShortcutsModal
        isOpen={keyboardShortcutsOpen}
        onClose={() => setKeyboardShortcutsOpen(false)}
      />

      {/* Quick Ella Prompt */}
      <QuickEllaPrompt
        isOpen={quickEllaOpen}
        onClose={() => setQuickEllaOpen(false)}
      />
    </React.Fragment>
  );
};

export default NavBar;
